import { Accordion } from "components/ui/Accordion";
import Spinner from "components/ui/Spinner";
import { SearchBox } from "components/ui/SearchBox";
import { useState, useEffect, useRef, useMemo, ReactNode } from "react";
import { isCondoAccessDeniedError } from "utils/accessibleCondo";
import { formatTimestamp } from "utils/dateFormat";
import { contactListItemClasses } from "utils/contactListItemClasses";
import { useAuth } from "context/AuthContext";
import { ReplyMuteBadge } from "components/ReplyMuteBadge";
import type { ReplyMute } from "types/ReplyMute";

interface ContactListItem {
  id: number;
  name: string;
  subtitle?: string;
  lastMessageAt?: string | null;
  hasUnread?: boolean;
  replyMute?: ReplyMute | null;
}

export interface ContactListRenderContext<T extends ContactListItem> {
  filteredItems: T[];
  filteredIndex: number;
}

interface ContactListProps<T extends ContactListItem> {
  listId: string;
  title: string;
  icon: ReactNode;
  items: T[];
  loading: boolean;
  error: string | null;
  searchPlaceholder: string;
  selectedId: number | null;
  isExpanded?: boolean;
  onToggle?: (expanded: boolean) => void;
  onSelectItem: (id: number) => void;
  getItemName: (item: T) => string;
  getItemSubtitle?: (item: T) => string;
  renderTitle?: (hasSelected: boolean, isExpanded: boolean) => ReactNode;
  renderItem?: (
    item: T,
    isSelected: boolean,
    context: ContactListRenderContext<T>
  ) => ReactNode;
  /** When set, the number in the group header counts only items for which this returns true (after search filter). */
  countItemsForHeader?: (item: T) => boolean;
  /** React list key; defaults to item.id. Use when item.id is not unique (e.g. one contact, multiple units). */
  getItemKey?: (item: T) => string | number;
  /** Id used for selection and onSelectItem; defaults to item.id. */
  getSelectionId?: (item: T) => number;
  /** When false, the row is not clickable and does not change selection. */
  isItemSelectable?: (item: T) => boolean;
  disableAutoExpand?: boolean;
  /** Hide per-section search when the list has fewer items than this (default 7). */
  searchMinItems?: number;
}

export function ContactList<T extends ContactListItem>({
  listId,
  title,
  icon,
  items,
  loading,
  error,
  searchPlaceholder,
  selectedId,
  isExpanded: controlledExpanded,
  onToggle,
  onSelectItem,
  getItemName,
  getItemSubtitle,
  renderTitle,
  renderItem,
  countItemsForHeader,
  getItemKey,
  getSelectionId,
  isItemSelectable,
  disableAutoExpand = false,
  searchMinItems = 7,
}: ContactListProps<T>) {
  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";
  const [searchQuery, setSearchQuery] = useState("");
  const resolveItemKey = getItemKey ?? ((item: T) => item.id);
  const resolveSelectionId = getSelectionId ?? ((item: T) => item.id);

  // Handle internal or controlled expanded state
  const isControlled = controlledExpanded !== undefined;
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isExpanded = isControlled ? controlledExpanded : internalExpanded;

  // Auto-expand when selection changes or on initial load with selected item
  const hasSelectedItem = items.some(
    (item) => resolveSelectionId(item) === selectedId
  );
  const prevSelectedIdRef = useRef<number | null>(null);
  const hasExpandedForInitialSelection = useRef(false);

  useEffect(() => {
    if (disableAutoExpand) {
      // Skip auto-expand logic entirely if disabled
      prevSelectedIdRef.current = selectedId;
      return;
    }

    // On initial load with a selected item (including after data loads),
    // expand if this list has the selected item and we haven't expanded yet
    if (
      !hasExpandedForInitialSelection.current &&
      hasSelectedItem &&
      selectedId !== null
    ) {
      if (isControlled) {
        onToggle?.(true);
      } else {
        setInternalExpanded(true);
      }
      hasExpandedForInitialSelection.current = true;
    }
    // On subsequent changes, only auto-expand when selectedId changes
    else if (
      hasExpandedForInitialSelection.current &&
      selectedId !== prevSelectedIdRef.current &&
      hasSelectedItem
    ) {
      if (isControlled) {
        onToggle?.(true);
      } else {
        setInternalExpanded(true);
      }
    }

    prevSelectedIdRef.current = selectedId;
  }, [selectedId, hasSelectedItem, isControlled, onToggle, disableAutoExpand]);

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    return items.filter((item) =>
      getItemName(item).toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [items, searchQuery, getItemName]);

  const headerCount = useMemo(() => {
    if (countItemsForHeader) {
      return filteredItems.filter(countItemsForHeader).length;
    }
    return filteredItems.length;
  }, [filteredItems, countItemsForHeader]);

  // Access-denied errors must not render as bare red rows under neighboring
  // sections (Administración / Otros) — that looked like cross-tenant counts.
  if (isCondoAccessDeniedError(error)) {
    return null;
  }

  if (error) {
    return (
      <div className="mt-4 p-4 text-red-500 text-xs lg:text-sm">{error}</div>
    );
  }

  const accordionItems = [
    {
      id: listId,
      title: renderTitle ? (
        renderTitle(hasSelectedItem, isExpanded!)
      ) : (
        <span
          className={`flex items-center ${
            hasSelectedItem ? "font-semibold text-blue-600" : ""
          }`}
        >
          {icon}
          {title} ({headerCount})
        </span>
      ),
      content: (
        <div>
          {loading && (
            <div className="flex justify-center py-3" aria-busy="true">
              <Spinner />
            </div>
          )}
          {items.length >= searchMinItems && (
            <SearchBox
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={searchPlaceholder}
            />
          )}
          <ul className="space-y-1">
            {filteredItems.map((item, filteredIndex) => {
              const selectionId = resolveSelectionId(item);
              const isSelected = selectedId === selectionId;
              const selectable = isItemSelectable
                ? isItemSelectable(item)
                : true;
              const renderContext: ContactListRenderContext<T> = {
                filteredItems,
                filteredIndex,
              };
              if (renderItem) {
                return (
                  <li
                    key={resolveItemKey(item)}
                    onClick={
                      selectable ? () => onSelectItem(selectionId) : undefined
                    }
                    className={selectable ? "cursor-pointer" : undefined}
                  >
                    {renderItem(item, isSelected, renderContext)}
                  </li>
                );
              }
              return (
                <li
                  key={resolveItemKey(item)}
                  onClick={
                    selectable ? () => onSelectItem(selectionId) : undefined
                  }
                  className={contactListItemClasses(isSelected)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs lg:text-sm truncate ${
                        item.hasUnread ? "font-bold" : "font-medium"
                      }`}>
                        {getItemName(item)}
                      </div>
                      {getItemSubtitle && (
                        <div className="text-xs text-gray-500">
                          {getItemSubtitle(item)}
                        </div>
                      )}
                      <ReplyMuteBadge mute={item.replyMute} locale={locale} />
                    </div>
                    {item.lastMessageAt && (
                      <div className="text-xs text-gray-400 flex-shrink-0">
                        {formatTimestamp(item.lastMessageAt, locale)}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ),
    },
  ];

  const handleToggle = (itemId: string | null) => {
    const newExpanded = itemId === listId;
    if (isControlled) {
      onToggle?.(newExpanded);
    } else {
      setInternalExpanded(newExpanded);
    }
  };

  return (
    <div
      className={`mt-4 ${isExpanded ? "pb-4 border-b border-gray-200" : ""}`}
    >
      <Accordion
        items={accordionItems}
        singleOpen={true}
        openItemId={isExpanded ? listId : null}
        onToggle={handleToggle}
      />
    </div>
  );
}
